<template>
    <div ref="emailConsultItem" class="email-preview">
        <!-- HEADER -->
        <div class="email-preview__header">
            <div class="email-preview__address-details">
                <CompassAvatar size="small" :initials="senderInitials" />
                <div class="email-preview__address-details-wrapper">
                    <div class="email-preview__sender">
                        <div
                            class="email-preview__sender-name typographies-paragraph-medium-bold"
                        >
                            {{ email.from.name }}
                        </div>
                        <div
                            class="email-preview__sender-email typographies-paragraph-small-regular"
                        >
                            ({{ email.from.email }})
                        </div>
                    </div>
                    <div class="email-preview__recipients">
                        <div class="email-preview__recipients-label">
                            {{ $t.__('email.EmailConsultPanelItem.to') }}:
                        </div>
                        <div class="email-preview__recipients-list">
                            {{ formattedRecipients }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="email-preview__extra-information">
                <div>
                    <div class="email-preview__sent-date typographies-paragraph-small-regular">
                        {{ formattedSentDate }}
                    </div>
                    <div
                        v-if="attachmentCount > 0"
                        class="email-preview__attachments-count typographies-paragraph-small-bold"
                    >
                        <CompassIcon size="small" icon="attachment" />
                        {{ formattedAttachmentCount }}
                    </div>
                </div>
            </div>
        </div>
        <!-- CONTENT -->
        <div class="email-preview__content typographies-paragraph-medium-regular">
            <EmailRelatedObject
                v-if="thread.length && thread[0].linked_object"
                :object="thread[0].linked_object"
            />
            <div
                class="email-preview__content-text"
                :class="editorClass"
                v-html="addBrToEmptyPragraphs(getContentEmail)"
            />
        </div>
        <div v-if="attachmentCount > 0" class="email-preview__attachments">
            <div class="email-preview__attachments-summary">
                <span class="typographies-paragraph-medium-bold">
                    {{ formattedAttachmentCount }}
                </span>
                <span
                    class="email-preview__attachments-weight typographies-paragraph-medium-regular"
                    >({{ formatBytes(attachmentsSize) }} au total)</span
                >
            </div>
            <div class="email-preview__attachments-list">
                <EmailAttachmentLink
                    v-for="attachment in email.attachments"
                    :key="attachment.id"
                    :file="attachment"
                />
            </div>
        </div>
    </div>
</template>
<script>
import moment from 'moment';
import EmailRelatedObject from 'vcomponents/email/EmailRelatedObject.vue';
import EmailAttachmentLink from 'vcomponents/email/EmailAttachmentLink.vue';
import { addBrToEmptyPragraphs, editorClass } from 'Helpers/richTextHelper.js';

export default {
    components: { EmailRelatedObject, EmailAttachmentLink },
    props: {
        email: {
            type: Object,
            required: true,
        },
        thread: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        editorClass,
        senderInitials() {
            const { from } = this.email;
            const [firstName, lastName] = from.name ? from.name.split(' ') : [null, null];
            const initials = `${firstName ? firstName[0] : ''}${lastName ? lastName[0] : ''}`;
            return initials;
        },
        attachmentCount() {
            return this.email.attachments.length;
        },
        attachmentsSize() {
            return this.email.attachments
                .map((item) => item.size)
                .reduce((sum, value) => sum + value, 0);
        },
        formattedAttachmentCount() {
            return this.$l10n.n__(
                '%(nb)d pièce-jointe',
                '%(nb)d pièce-jointes',
                this.attachmentCount,
                { nb: this.attachmentCount },
            );
        },
        formattedSentDate() {
            const locale = window.App.lang;
            moment.locale(locale);

            const momentDate = moment(this.email.created);
            const separator = this.$t.__('email.EmailConsultPanelItem.dateTo');

            let formattedDate =
                locale === 'fr'
                    ? momentDate.format(`ddd DD MMM YYYY [${separator}] HH:mm`)
                    : momentDate.format(`ddd, MMM DD, YYYY [${separator}] HH:mm`);

            const differenceBetweenSentDateAndNow = moment().diff(
                moment(this.email.created),
                'days',
            );
            if (differenceBetweenSentDateAndNow > 0 && differenceBetweenSentDateAndNow <= 14) {
                formattedDate += ` (${moment(this.email.created).fromNow()})`;
            }

            return formattedDate;
        },
        formattedRecipients() {
            const to = this.email.to
                .map((item) => {
                    if (item.name && item.email) {
                        return `${item.name} (${item.email})`;
                    }
                    return item.name || item.email;
                })
                .filter((item) => item);

            const cc = this.email.cc.map((item) => item.name).filter((name) => name);
            const bcc = this.email.bcc.map((item) => item.name).filter((name) => name);

            const combined = [...to, ...cc, ...bcc];
            const uniqueItems = new Set(combined);

            return Array.from(uniqueItems)
                .filter((item) => item)
                .join(', ');
        },
        getContentEmail() {
            // Data from timeline or listing email
            return this.email.description || this.email.content;
        },
    },
    methods: {
        addBrToEmptyPragraphs,
        formatBytes(bytes, decimals = 2) {
            if (!+bytes) return '0' + this.$l10n.n__(' Octet');

            const k = 1000;
            const dm = decimals < 0 ? 0 : decimals;
            const sizes = ['Octets', 'Ko', 'Mo', 'Go', 'To', 'Po', 'Eo', 'Zo', 'Yo'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${this.$l10n.n__(
                '%s',
                '%s',
                sizes[i],
                sizes[i],
            )}`;
        },
    },
};
</script>
<style lang="scss" scoped>
.email-preview {
    padding: var(--spacing-3) 0;
    width: 100%;
    word-break: break-word;

    &__header {
        display: flex;
        margin: 0 0 var(--spacing-2);
        justify-content: space-between;

        &--snippet {
            display: flex;
            justify-content: space-between;
            gap: var(--spacing-3);

            &-wrapper {
                width: 100%;
                min-width: 0; // to stop overflow-x due to snippet white space
            }

            &-information {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
            }
        }
    }

    &__address-details {
        display: flex;
        gap: var(--spacing-3);
        min-width: 0;

        &-wrapper {
            min-width: 0;
        }
    }

    &__sender {
        align-items: baseline;
        flex-wrap: wrap;
        display: flex;

        &-name {
            margin-right: var(--spacing-0);
        }

        &-email {
            color: var(--colors-common-text-standard-caption);
        }
    }

    &__recipients {
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: var(--spacing-0);
        font-size: 12px;
        line-height: 20px;

        &-list {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__extra-information {
        display: flex;
        flex-shrink: 0;
        gap: var(--spacing-2);
    }

    &__sent-date {
        align-items: center;
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: 4px;
        margin: 2px 0 1px;
    }

    &__attachments-count {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: var(--spacing-0);
        color: var(--colors-common-text-subtle-default);
    }

    &__content {
        padding: 0 0 0 48px;

        &-text {
            :deep(ul) {
                padding-left: 24px;
                list-style-type: disc;
            }
        }
    }

    &__attachments {
        border-top: 1px solid var(--colors-common-border-lighter);
        margin: var(--spacing-3) 0 0 var(--spacing-5);
        padding: var(--spacing-2) 0 0;

        &-summary {
            margin: 0 0 var(--spacing-1);
        }

        &-weight {
            color: var(--colors-common-text-standard-caption);
        }

        &-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--spacing-0);
        }
    }

    &__snippet {
        color: var(--colors-common-text-standard-caption);
        font-family: var(--font-families-lato);
        font-size: 12px;
        font-weight: 400;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        :deep() {
            p:last-of-type {
                margin-bottom: 0;
            }
        }
    }
}
</style>
